/**
 * Question API calls
 */

import apiClient from './client.js';
import { Question, QuestionWithOptions, CreateQuestionInput, UpdateQuestionInput, CreateAnswerOptionPayload, UpdateAnswerOptionInput } from '../../types/models';

export const questionsApi = {
  /**
   * Create a question
   */
  create: async (data: CreateQuestionInput): Promise<Question> => {
    const response = await apiClient.post('/questions', data);
    return response.data;
  },

  /**
   * Get question by ID with options
   */
  getById: async (id: string): Promise<QuestionWithOptions> => {
    const response = await apiClient.get(`/questions/${id}`);
    return response.data;
  },

  /**
   * Update question
   */
  update: async (id: string, data: UpdateQuestionInput): Promise<Question> => {
    const response = await apiClient.patch(`/questions/${id}`, data);
    return response.data;
  },

  /**
   * Delete question
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/questions/${id}`);
  },

  /**
   * Create answer options for a question
   */
  createOptions: async (questionId: string, options: CreateAnswerOptionPayload[]): Promise<any[]> => {
    // Validate inputs
    if (!questionId || typeof questionId !== 'string') {
      throw new TypeError(`Invalid questionId: expected string, got ${typeof questionId}`);
    }
    if (!Array.isArray(options)) {
      throw new TypeError(`Invalid options: expected array, got ${typeof options}`);
    }
    
    const response = await apiClient.post(`/questions/${questionId}/options`, { options });
    return response.data;
  },

  /**
   * Update answer option
   */
  updateOption: async (questionId: string, optionId: string, data: UpdateAnswerOptionInput): Promise<any> => {
    const response = await apiClient.patch(`/questions/${questionId}/options/${optionId}`, data);
    return response.data;
  },

  /**
   * Delete answer option
   */
  deleteOption: async (questionId: string, optionId: string): Promise<void> => {
    await apiClient.delete(`/questions/${questionId}/options/${optionId}`);
  },

  /**
   * Upload a prompt image for a question
   */
  uploadImage: async (
    id: string,
    file: File,
  ): Promise<{ prompt_media_url: string; prompt_media_width: number | null; prompt_media_height: number | null }> => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await apiClient.post(`/questions/${id}/upload-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Delete a question's prompt image
   */
  deleteImage: async (id: string): Promise<void> => {
    await apiClient.delete(`/questions/${id}/image`);
  },

  /**
   * Upload an image for an answer option
   */
  uploadOptionImage: async (
    questionId: string,
    optionId: string,
    file: File,
  ): Promise<{ option_media_url: string; option_media_width: number | null; option_media_height: number | null }> => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await apiClient.post(
      `/questions/${questionId}/options/${optionId}/upload-image`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  /**
   * Delete an answer option's image
   */
  deleteOptionImage: async (questionId: string, optionId: string): Promise<void> => {
    await apiClient.delete(`/questions/${questionId}/options/${optionId}/image`);
  },
};
